import type { Plugin } from 'vue'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { createPinia } from 'pinia'
import { createORM } from 'pinia-orm'
import { createPiniaOrmAxios } from '@pinia-orm/axios'
import { installShardedPersist } from '@/plugins/persistShardedStores'
import axios from 'axios'

export const datalayer: Plugin = (app) => {
    // implicit data layer manager
    const pinia = createPinia()

    pinia.use(piniaPluginPersistedstate)

    pinia.use(({ store }) => {
        store.$onAction(({ name, args, onError }) => {
            onError((error) => {
                console.error('PINIA ACTION ERROR', {
                    store: store.$id,
                    action: name,
                    args,
                    error,
                })
            })
        })
    })

    pinia.use(createORM({
        plugins: [
            createPiniaOrmAxios({
                axios,
                baseURL: 'https://api.sailing.cz',
                timeout: 120000,
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
            }),
        ],
    }))

    // try to use sharding for speed-up
    installShardedPersist(pinia, {
        storage: window.localStorage,
        indexKey: 'orm:index',
        entities: [
            {
                matchStoreId: 'events',
                keyPrefix: 'orm:events',
                shardKeyOf: (rec) => rec.id ?? null,
            },
            {
                matchStoreId: 'events-referees',
                keyPrefix: 'orm:events-referees',
                shardKeyOf: (rec) => rec.eventId ?? null,
            },
            {
                matchStoreId: 'events-lifeguards',
                keyPrefix: 'orm:events-lifeguards',
                shardKeyOf: (rec) => rec.eventId ?? null,
            },
            {
                matchStoreId: 'events-classes',
                keyPrefix: 'orm:events-classes',
                shardKeyOf: (rec) => rec.eventId ?? null,
            },
            {
                matchStoreId: 'events-races',
                keyPrefix: 'orm:events-races',
                shardKeyOf: (rec) => rec.eventId ?? null,
            },
            {
                matchStoreId: 'events-courses',
                keyPrefix: 'orm:events-courses',
                shardKeyOf: (rec) => rec.eventId ?? null,
            },
            {
                matchStoreId: 'events-courses-objects',
                keyPrefix: 'orm:events-courses-objects',
                shardKeyOf: (rec) => rec.eventId ?? null,
            },
            {
                matchStoreId: 'events-boats',
                keyPrefix: 'orm:events-boats',
                shardKeyOf: (rec) => rec.eventId ?? null,
            },
            {
                matchStoreId: 'events-crew',
                keyPrefix: 'orm:events-crew',
                shardKeyOf: (rec) => rec.eventId ?? null,
                migration: {
                    shardVia: {
                        sourceStoreId: 'events-boats',
                        localFk: 'eventBoatId',
                        sourcePk: 'id',
                        sourceValueField: 'eventId',
                    },
                },
            },
            {
                matchStoreId: 'events-categories',
                keyPrefix: 'orm:events-categories',
                shardKeyOf: (rec) => rec.eventId ?? null,
                migration: {
                    shardVia: {
                        sourceStoreId: 'events-classes',
                        localFk: 'eventClassId',
                        sourcePk: 'id',
                        sourceValueField: 'eventId',
                    },
                },
            },
            {
                matchStoreId: 'events-results',
                keyPrefix: 'orm:events-results',
                shardKeyOf: (rec) => rec.eventId ?? null,
                migration: {
                    shardVia: {
                        sourceStoreId: 'events-races',
                        localFk: 'eventRaceId',
                        sourcePk: 'id',
                        sourceValueField: 'eventId',
                    },
                },
            },
            {
                matchStoreId: 'events-rounds',
                keyPrefix: 'orm:events-rounds',
                shardKeyOf: (rec) => rec.eventId ?? null,
            },
            {
                matchStoreId: 'event-heat-results',
                keyPrefix: 'orm:event-heat-results',
                shardKeyOf: (rec) => rec.eventId ?? null,
            },
        ],
    })

    // apply data layer to passed application
    app.use(pinia)
}
